import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { reportService, UserTimeSheet as UserTimeSheetData, DayTimeSheet } from '../services/reportService';

const UserTimeSheet: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const userId = searchParams.get('user_id');
  const dateParam = searchParams.get('date');
  
  const [timeSheet, setTimeSheet] = useState<UserTimeSheetData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (userId && dateParam) {
      loadTimeSheet();
    } else {
      setError('Missing required parameters: user_id and date');
      setLoading(false);
    }
  }, [userId, dateParam]);

  const loadTimeSheet = async () => {
    if (!userId || !dateParam) return;

    setLoading(true);
    setError('');
    try {
      // Create a date range for the week containing the selected date
      const selectedDate = new Date(dateParam);
      
      // Find the Monday of the week
      const dayOfWeek = selectedDate.getDay();
      const diff = selectedDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      const monday = new Date(selectedDate.setDate(diff));
      
      // Find the Sunday of the week
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      const formatDate = (date: Date) => date.toISOString().split('T')[0];

      const data = await reportService.getUserTimeSheet({
        user_id: parseInt(userId),
        start_date: formatDate(monday),
        end_date: formatDate(sunday),
      });
      
      setTimeSheet(data);
    } catch (err: any) {
      console.error('Error fetching time sheet:', err);
      setError(err.response?.data?.message || 'Failed to fetch time sheet');
    } finally {
      setLoading(false);
    }
  };

  const formatDateRange = () => {
    if (!timeSheet?.days || timeSheet.days.length === 0) return '';
    
    const firstDay = new Date(timeSheet.days[0].date);
    const lastDay = new Date(timeSheet.days[timeSheet.days.length - 1].date);
    
    const formatOptions: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    };
    
    const firstFormatted = firstDay.toLocaleDateString('en-US', formatOptions);
    const lastFormatted = lastDay.toLocaleDateString('en-US', formatOptions);
    
    return `${firstFormatted} - ${lastFormatted}`;
  };

  const renderDayEntries = (day: DayTimeSheet) => {
    const dayDate = new Date(day.date);
    const dateFormatted = dayDate.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });

    return (
      <Box key={day.date} sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
          {dateFormatted}
        </Typography>
        
        {day.entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mb: 2 }}>
            No time entered.
          </Typography>
        ) : (
          <>
            {day.entries.map((entry, index) => (
              <Box
                key={entry.id}
                sx={{
                  ml: 2,
                  mb: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, minWidth: '200px' }}>
                    {entry.activity}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: '150px' }}>
                    {entry.user_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'success.main' }}>
                    {entry.duration_formatted}
                  </Typography>
                </Box>
              </Box>
            ))}
            <Box sx={{ ml: 2, mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Total hours for {day.day_of_week}: {day.total_duration_formatted}
              </Typography>
            </Box>
          </>
        )}
        <Divider sx={{ mt: 2 }} />
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/reports/user-time')}
          sx={{ mb: 2 }}
        >
          Back to Report
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!timeSheet) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/reports/user-time')}
          sx={{ mb: 2 }}
        >
          Back to Report
        </Button>
        <Alert severity="info">No time sheet data available</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/reports/user-time')}
        sx={{ mb: 2 }}
      >
        Back to Report
      </Button>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Activity
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary', mb: 3 }}>
          « {formatDateRange()} »
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
          User: <strong>{timeSheet.user.name}</strong>
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {timeSheet.days.map((day) => renderDayEntries(day))}
      </Paper>
    </Box>
  );
};

export default UserTimeSheet;

